import { RefObject, TouchEvent, useCallback, useEffect, useRef, useState } from 'react';

/** Drag distance (after resistance) at which a release fires the action. */
const THRESHOLD = 64;
/** Hard cap on the rendered pull, so a long drag can't push the list off screen. */
const MAX_PULL = 96;
/** Finger travel is halved so the pull feels weighted rather than 1:1 with the thumb. */
const RESISTANCE = 0.5;

interface UsePullGestureOptions {
  /** The scroll container whose top edge arms the gesture. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Released past the threshold while at the TOP edge. */
  onPullDown?: () => void;
  /** Gate for the gesture (e.g. a refresh already in flight). */
  canPullDown?: boolean;
}

export interface PullState {
  /** Post-resistance drag distance in px, clamped to MAX_PULL. Drives the indicator. */
  distance: number;
  /** Past the threshold — the indicator should read as "release to trigger". */
  armed: boolean;
}

interface UsePullGestureResult extends PullState {
  /** Attach all of these to the scroll container — `onTouchCancel` is not optional, see below. */
  handlers: {
    onTouchStart: (event: TouchEvent<HTMLElement>) => void;
    onTouchMove: (event: TouchEvent<HTMLElement>) => void;
    onTouchEnd: () => void;
    onTouchCancel: () => void;
  };
}

const IDLE: PullState = { distance: 0, armed: false };

/**
 * Pull-to-refresh for a scroll container.
 *
 * The gesture only engages when the container is already at the top, so it never competes with normal
 * scrolling: a drag that begins mid-list scrolls the list and is ignored here. Once engaged it stays
 * engaged for the rest of the drag, so dragging back past the origin collapses the pull without ending
 * the gesture — dragging forward again resumes it rather than needing a fresh touch.
 *
 * Deliberately does NOT call `preventDefault` — React attaches touch listeners passively, so the call
 * would be a no-op plus a console error. The container's `overscroll-y-contain` handles scroll chaining;
 * any residual native rubber-banding rides underneath the translated content.
 */
export const usePullGesture = ({
  scrollRef,
  onPullDown,
  canPullDown = true,
}: UsePullGestureOptions): UsePullGestureResult => {
  const [pull, setPull] = useState<PullState>(IDLE);
  const startY = useRef<number | null>(null);
  // touchmove fires far faster than the display refreshes. Committing every event would queue ~60+ React
  // renders a second and the pull visibly stutters, so coalesce to at most one commit per frame.
  const frame = useRef<number | null>(null);
  const pending = useRef<PullState | null>(null);
  // Tracked separately from the handle: a valid rAF id can be 0, so `frame.current !== null` is not a safe
  // "already scheduled" test — it would wedge every later commit.
  const scheduled = useRef(false);
  // Whether this drag qualified as a pull. Held in a ref, not state: the touchend handler closes over a
  // render snapshot, so reading state there can miss a gesture that engaged during this same drag.
  const engagedRef = useRef(false);
  const armedRef = useRef(false);

  // Commit on the next frame, collapsing any moves that arrived in between.
  const commit = useCallback((next: PullState): void => {
    pending.current = next;
    if (scheduled.current) return;
    scheduled.current = true;
    frame.current = requestAnimationFrame(() => {
      scheduled.current = false;
      frame.current = null;
      // Apply only the LATEST position — intermediate moves within the frame are dropped, not replayed.
      if (pending.current) setPull(pending.current);
    });
  }, []);

  /** Flush immediately — a release must not wait a frame, or the collapse looks like a dropped gesture. */
  const commitNow = useCallback((next: PullState): void => {
    if (scheduled.current && frame.current !== null) cancelAnimationFrame(frame.current);
    scheduled.current = false;
    frame.current = null;
    pending.current = null;
    setPull(next);
  }, []);

  useEffect(
    () => () => {
      if (scheduled.current && frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>): void => {
    startY.current = event.touches[0]?.clientY ?? null;
    engagedRef.current = false;
    armedRef.current = false;
  }, []);

  const onTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>): void => {
      const element = scrollRef.current;
      const origin = startY.current;
      const currentY = event.touches[0]?.clientY;
      if (!element || origin === null || currentY === undefined) return;

      const delta = currentY - origin;

      // Engage on the first downward move made from the top edge, then hold it for the rest of the drag.
      if (!engagedRef.current) {
        if (delta > 0 && element.scrollTop <= 0 && canPullDown && onPullDown) {
          engagedRef.current = true;
        } else {
          return;
        }
      }

      // A drag back past the origin collapses the pull but stays engaged, so dragging forward again
      // resumes the same pull.
      if (delta <= 0) {
        armedRef.current = false;
        commitNow(IDLE);
        return;
      }

      const distance = Math.min(delta * RESISTANCE, MAX_PULL);
      armedRef.current = distance >= THRESHOLD;
      commit({ distance, armed: armedRef.current });
    },
    [scrollRef, canPullDown, onPullDown, commit, commitNow],
  );

  const onTouchEnd = useCallback((): void => {
    const armed = armedRef.current;
    startY.current = null;
    engagedRef.current = false;
    armedRef.current = false;
    commitNow(IDLE);
    if (armed) onPullDown?.();
  }, [onPullDown, commitNow]);

  /**
   * MUST be wired up. iOS WKWebView fires `touchcancel` whenever the system takes over a touch — an
   * incoming call, a notification, an orientation change — and React's touch listeners are passive, so the
   * UA can do that at any moment. Without this handler `touchend` never arrives, the refs stay set, and
   * `distance` stays non-zero: the content block would sit translated indefinitely, until the next touch.
   * A cancel is NOT a release, so it deliberately does not fire the callback.
   */
  const onTouchCancel = useCallback((): void => {
    startY.current = null;
    engagedRef.current = false;
    armedRef.current = false;
    commitNow(IDLE);
  }, [commitNow]);

  return { ...pull, handlers: { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel } };
};
